<template>
  <div
    class="flex flex-col border rounded-md transition-colors overflow-hidden"
    :class="[
      errorStatus
        ? 'border-red-500 '
        : 'border-gray-300 focus-within:border-main focus-within:ring-1 focus-within:ring-main',
    ]"
  >
    <div
      v-if="editor"
      class="flex flex-wrap items-center gap-1 p-1.5 border-b border-gray-200 bg-gray-50"
    >
      <button
        type="button"
        @click.stop="editor.chain().focus().toggleBold().run()"
        class="p-1.5 rounded transition-colors hover:bg-gray-200"
        :class="{ 'bg-main text-white hover:bg-main-hover': isBoldActive }"
        :title="$t('forms.editor.bold')"
      >
        <Icon icon="mdi:format-bold" class="w-5 h-5" />
      </button>

      <button
        type="button"
        @click.stop="editor.chain().focus().toggleItalic().run()"
        class="p-1.5 rounded transition-colors hover:bg-gray-200"
        :class="{ 'bg-main text-white hover:bg-main-hover': isItalicActive }"
        :title="$t('forms.editor.italic')"
      >
        <Icon icon="mdi:format-italic" class="w-5 h-5" />
      </button>

      <button
        type="button"
        @click.stop="editor.chain().focus().toggleStrike().run()"
        class="p-1.5 rounded transition-colors hover:bg-gray-200"
        :class="{ 'bg-main text-white hover:bg-main-hover': isStrikeActive }"
        :title="$t('forms.editor.strikethrough')"
      >
        <Icon icon="mdi:format-strikethrough" class="w-5 h-5" />
      </button>

      <div class="w-px h-6 bg-gray-300 mx-1"></div>

      <Menu as="div" class="relative inline-block text-left">
        <MenuButton
          class="inline-flex items-center gap-1 px-2 py-1.5 text-sm font-medium rounded transition-colors hover:bg-gray-200"
          :class="{ 'bg-main text-white hover:bg-main-hover': isHeadingActive }"
        >
          <span>{{ $t('myMap.export.form.title.label') }}</span>
          <Icon icon="mdi:chevron-down" class="w-4 h-4" />
        </MenuButton>

        <transition
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-in"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <MenuItems
            class="absolute left-0 mt-1 w-32 origin-top-left divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-20"
          >
            <div class="px-1 py-1">
              <MenuItem v-for="level in [1, 2, 3]" :key="level" v-slot="{ active }">
                <button
                  type="button"
                  @click="toggleHeading(level)"
                  :class="[
                    editor.isActive('heading', { level })
                      ? 'bg-main text-white'
                      : active
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700',
                    'group flex w-full items-center rounded-sm px-2 py-2 text-sm font-bold',
                  ]"
                >
                  {{ $t('myMap.export.form.title.label') }} {{ level }}
                </button>
              </MenuItem>
              <MenuItem v-slot="{ active }">
                <button
                  type="button"
                  @click="editor.chain().focus().setParagraph().run()"
                  :class="[
                    isParagraphActive
                      ? 'bg-main text-white'
                      : active
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-700',
                    'group flex w-full items-center rounded-sm px-2 py-2 text-sm',
                  ]"
                >
                  Paragraphe
                </button>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>

      <div class="w-px h-6 bg-gray-300 mx-1"></div>

      <button
        type="button"
        @click.stop="editor.chain().focus().toggleBulletList().run()"
        class="p-1.5 rounded transition-colors hover:bg-gray-200"
        :class="{ 'bg-main text-white hover:bg-main-hover': isBulletListActive }"
      >
        <Icon icon="mdi:format-list-bulleted" class="w-5 h-5" />
      </button>
    </div>

    <div class="p-4 min-h-40 cursor-text bg-white">
      <editor-content :editor="editor" class="tiptap-editor-content" />
    </div>

    <div
      v-if="errorStatus && editor"
      class="px-4 py-2 bg-red-50 border-t border-red-100 text-red-600 text-xs"
    >
      <span v-if="editor.getText().length > props.maxLength">
        {{ $t('forms.errorMessages.maxlength', { max: maxLength }) }}
      </span>
      <span v-else-if="editor.getText().length < props.minLength">
        {{ $t('forms.errorMessages.minlength', { min: minLength }) }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import StarterKit from '@tiptap/starter-kit'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { computed, watch, type ModelRef } from 'vue'

// Utilisation de defineModel (Vue 3.4+)
const contentModel: ModelRef<string> = defineModel('contentModel', { required: true })

const props = withDefaults(
  defineProps<{
    parentFormError: boolean
    minLength?: number
    maxLength?: number
  }>(),
  {
    minLength: 50,
    maxLength: Infinity,
  },
)

// Configuration de l'éditeur avec useEditor pour la réactivité
const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3],
      },
    }),
  ],
  content: contentModel.value,
  editorProps: {
    attributes: {
      class: 'focus:outline-none min-h-[150px]',
    },
  },
  onUpdate: ({ editor }) => {
    contentModel.value = editor.getHTML()
  },
})

// Synchronisation inverse : Si le parent change le contenu
watch(contentModel, (newValue) => {
  const isSame = editor.value?.getHTML() === newValue
  if (!isSame && editor.value) {
    editor.value.commands.setContent(newValue, { emitUpdate: false })
  }
})

// Computed properties pour les états actifs des boutons
const isBoldActive = computed(() => editor.value?.isActive('bold') ?? false)
const isItalicActive = computed(() => editor.value?.isActive('italic') ?? false)
const isStrikeActive = computed(() => editor.value?.isActive('strike') ?? false)
const isBulletListActive = computed(() => editor.value?.isActive('bulletList') ?? false)

const isHeadingActive = computed(() => {
  if (!editor.value) return false
  return (
    editor.value.isActive('heading', { level: 1 }) ||
    editor.value.isActive('heading', { level: 2 }) ||
    editor.value.isActive('heading', { level: 3 })
  )
})

const isParagraphActive = computed(() => {
  if (!editor.value) return false
  return editor.value.isActive('paragraph') && !isHeadingActive.value
})

const errorStatus = computed(() => {
  if (!editor.value) return false
  const length = editor.value.getText().length
  return (length < props.minLength || length > props.maxLength) && props.parentFormError
})

const toggleHeading = (level: number) => {
  if (!editor.value) return

  if (editor.value.isActive('heading', { level })) {
    editor.value.chain().focus().setParagraph().run()
  } else {
    editor.value
      .chain()
      .focus()
      .setHeading({ level: level as 1 | 2 | 3 })
      .run()
  }
}
</script>

<style>
.tiptap-editor-content .ProseMirror {
  color: #1f2937;
  line-height: 1.625;
  outline: none;
  min-height: 150px;
}

.tiptap-editor-content .ProseMirror:focus {
  outline: none;
}

/* Titles */
.tiptap-editor-content h1 {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.tiptap-editor-content h2 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.tiptap-editor-content h3 {
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

/* Paragraphes */
.tiptap-editor-content p {
  margin-bottom: 0.5rem;
}

.tiptap-editor-content p:last-child {
  margin-bottom: 0;
}

/* Lists */
.tiptap-editor-content ul {
  list-style-type: disc;
  margin-left: 1.25rem;
  margin-bottom: 0.5rem;
  padding-left: 0.5rem;
}

.tiptap-editor-content ol {
  list-style-type: decimal;
  margin-left: 1.25rem;
  margin-bottom: 0.5rem;
  padding-left: 0.5rem;
}

.tiptap-editor-content li {
  margin-bottom: 0.25rem;
}

/* inline formating */
.tiptap-editor-content strong {
  font-weight: 700;
}

.tiptap-editor-content em {
  font-style: italic;
}

.tiptap-editor-content s {
  text-decoration: line-through;
}

/* Placeholder if needed */
.tiptap-editor-content .ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}
</style>
